import React, { useState, useEffect } from 'react';
import {
  Grid, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Stack, Box, Dialog, DialogTitle, DialogContent, DialogActions, Chip, FormGroup, FormControlLabel, Radio, Button
} from '@mui/material';
import ModernLayout from './common/ModernLayout';
import ModernCard, { StatsCard } from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import axiosInstance from './axiosConfig';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Thermostat, LocationOn, MeetingRoom, Save } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

const ProjectSettings = () => {
  const { translate } = useLanguage();
  const projectId = localStorage.getItem('projectId');
  const [temperatureUnit, setTemperatureUnit] = useState('Celsius');
  const [location, setLocation] = useState('');
  const [numRooms, setNumRooms] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [newRoomName, setNewRoomName] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchProjectSettings();
      fetchRooms();
    }
  }, [projectId]);

  const fetchProjectSettings = async () => {
    try {
      const response = await axiosInstance.get(`/api/projects/${projectId}/settings`);
      setTemperatureUnit(response.data.temperature_unit || 'Celsius');
      setLocation(response.data.location || '');
      setNumRooms(response.data.num_rooms || 0);
    } catch (error) {
      console.error('Error fetching project settings:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axiosInstance.get(`/api/rooms/${projectId}`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const handleSave = async () => {
    try {
      await axiosInstance.put(`/api/projects/${projectId}/settings`, {
        temperature_unit: temperatureUnit,
        location,
        num_rooms: numRooms,
      });
    } catch (error) {
      console.error('Error saving project settings:', error);
    }
  };

  const handleAddRoom = async () => {
    if (!newRoomName.trim()) return;
    try {
      await axiosInstance.post('/api/room', { name: newRoomName, projectId });
      setNewRoomName('');
      fetchRooms();
    } catch (error) {
      console.error('Error adding new room:', error);
    }
  };

  const handleDeleteRoom = async () => {
    try {
      await axiosInstance.delete(`/api/room/${roomToDelete}`);
      setRoomToDelete(null);
      setOpenDialog(false);
      fetchRooms();
    } catch (error) {
      console.error('Error deleting room:', error);
    }
  };

  return (
    <ModernLayout title={translate('projectSettings.title')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('projectSettings.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('projectSettings.subtitle')}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('projectSettings.temperatureUnit')}
            value={temperatureUnit}
            subtitle={translate('projectSettings.measurementScale')}
            icon={<Thermostat />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('projectSettings.location')}
            value={location || translate('projectSettings.noConfigured')}
            subtitle={translate('projectSettings.projectLocation')}
            icon={<LocationOn />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('projectSettings.roomsConfigured')}
            value={rooms.length.toString()}
            subtitle={translate('projectSettings.cultivationSpaces')}
            icon={<MeetingRoom />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('projectSettings.status')}
            value={translate('projectSettings.active')}
            subtitle={translate('projectSettings.projectInOperation')}
            icon={<SettingsIcon />}
            color="success"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Configuración del Proyecto */}
        <Grid item xs={12} md={6}>
          <ModernCard
            title={translate('projectSettings.generalConfiguration')}
            subtitle={translate('projectSettings.adjustBasicParameters')}
          >
            <Box sx={{ p: 2 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>{translate('projectSettings.temperatureUnit')}</InputLabel>
                <Select
                  value={temperatureUnit}
                  onChange={(e) => setTemperatureUnit(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <MenuItem value="Celsius">{translate('projectSettings.celsius')}</MenuItem>
                  <MenuItem value="Fahrenheit">{translate('projectSettings.fahrenheit')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={translate('projectSettings.location')}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                fullWidth
                margin="normal"
                placeholder={translate('projectSettings.locationPlaceholder')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <GradientButton
                  startIcon={<Save />}
                  onClick={handleSave}
                >
                  {translate('projectSettings.saveConfiguration')}
                </GradientButton>
              </Box>
            </Box>
          </ModernCard>
        </Grid>

        {/* Crear nueva room */}
        <Grid item xs={12} md={6}>
          <ModernCard
            title={translate('projectSettings.addNewRoom')}
            subtitle={translate('projectSettings.createCultivationSpace')}
          >
            <Box sx={{ p: 2 }}>
              <TextField
                label={translate('projectSettings.roomName')}
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                fullWidth
                margin="normal"
                placeholder={translate('projectSettings.roomNamePlaceholder')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <GradientButton
                  startIcon={<AddIcon />}
                  onClick={handleAddRoom}
                  disabled={!newRoomName.trim()}
                >
                  {translate('projectSettings.addRoom')}
                </GradientButton>
              </Box>
            </Box>
          </ModernCard>
        </Grid>
      </Grid>

      {/* Lista de Rooms */}
      <ModernCard
        title={translate('projectSettings.roomManagement')}
        subtitle={translate('projectSettings.manageProjectRooms')}
        sx={{ mt: 3 }}
      >
        <Box sx={{ p: 2 }}>
          {rooms.length > 0 ? (
            <Grid container spacing={2}>
              {rooms.map(room => (
                <Grid item xs={12} sm={6} md={4} key={room.id}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        <MeetingRoom />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {room.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {room.description || translate('projectSettings.noDescription')}
                        </Typography>
                      </Box>
                    </Box>
                    <GlassButton
                      onClick={() => {
                        setRoomToDelete(room.id);
                        setOpenDialog(true);
                      }}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </GlassButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {translate('projectSettings.noRoomsAssociated')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {translate('projectSettings.createFirstRoom')}
              </Typography>
            </Box>
          )}
        </Box>
      </ModernCard>

      {/* Dialogo de confirmación para eliminar */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'white',
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: 700,
          fontSize: '1.3rem',
        }}>
          {translate('projectSettings.confirmDeletion')}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
            {translate('projectSettings.confirmDeleteRoom')} {rooms.find(r => r.id === roomToDelete)?.name}?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'rgba(0, 0, 0, 0.6)' }}>
            {translate('projectSettings.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{ 
              textTransform: 'none',
              color: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {translate('projectSettings.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteRoom}
            color="error"
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
              },
            }}
          >
            {translate('projectSettings.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </ModernLayout>
  );
};

export default ProjectSettings;
